import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeContext } from '../contexts/ThemeContext';
import { useNavigation } from '../hooks/useNavigation';
import { useAuth } from '../contexts/AuthContext';
import { APP_NAME } from '../config/frontend';
import { 
  PanelLeftClose, 
  PanelLeftOpen, 
  ChevronDown, 
  ChevronRight
} from 'lucide-react';

const Sidebar = ({ 
  sidebarOpen, 
  setSidebarOpen, 
  sidebarVisible, 
  setSidebarVisible,
  activeDropdown,
  setActiveDropdown 
}) => {
  const { theme } = useContext(ThemeContext);
  const { user } = useAuth();
  const { navigationItems } = useNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const userClickedDropdown = useRef(false);
  const previousPath = useRef(location.pathname);
  
  // Reset user click flag when navigating to a different main section
  useEffect(() => {
    const currentSection = location.pathname.split('/')[1];
    const prevSection = previousPath.current.split('/')[1];
    
    if (currentSection !== prevSection) {
      userClickedDropdown.current = false;
    }
    previousPath.current = location.pathname;
  }, [location.pathname]);
  
  // Set active dropdown based on current location (only when on a child page)
  useEffect(() => {
    const activeIndex = navigationItems.findIndex(item => 
      item.children && item.children.some(child => child.active)
    );
    // Only auto-set dropdown if we're actually on a child page and user hasn't manually clicked
    if (activeIndex >= 0 && !userClickedDropdown.current) {
      setActiveDropdown(activeIndex);
    }
  }, [location.pathname, navigationItems, setActiveDropdown]);

  const handleCardClick = (href) => {
    navigate(href);
    // Close sidebar on mobile after navigation
    setSidebarOpen(false);
  };

  const handleDropdownToggle = (index) => {
    userClickedDropdown.current = true;
    setActiveDropdown(prev => prev === index ? null : index);
  };

  
  return (
    <div className={`sidebar-container fixed inset-y-0 left-0 z-30 bg-gradient-to-b from-red-50 via-white to-blue-50 shadow-2xl border-r border-red-200 transform transition-all duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${sidebarVisible ? 'lg:translate-x-0 lg:w-64' : 'lg:translate-x-0 lg:w-16'}`}>
      <div className="flex flex-col h-full overflow-hidden">
        {/* Logo */}
        <div className="p-4 border-b border-red-200 bg-gradient-to-r from-red-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-blue-600 rounded-full flex items-center justify-center relative overflow-hidden">
                  <span className="text-white font-bold text-lg z-10">G</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000"></div>
                </div>
              </div>
              <div className={`transition-all duration-300 ${!sidebarVisible ? 'lg:opacity-0 lg:w-0 lg:overflow-hidden' : ''}`}>
                <h1 className="text-lg font-bold bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent whitespace-nowrap">
                  {APP_NAME}
                </h1>
                <p className="text-xs text-gray-600 italic whitespace-nowrap">NexGen POS</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarVisible(!sidebarVisible)}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              {sidebarVisible ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className={`navigation-container flex-1 py-4 space-y-2 overflow-y-auto transition-all duration-300 ${sidebarVisible ? 'px-4' : 'lg:px-2'}`}>
          {navigationItems.map((item, index) => (
            <div key={item.name}>
              {item.children ? (
                <div>
                  <button
                    onClick={() => handleDropdownToggle(index)}
                    className={`w-full flex items-center justify-between py-3 rounded-xl text-left transition-all duration-300 group cursor-pointer relative overflow-hidden ${
                      activeDropdown === index || item.children.some(child => child.active)
                        ? 'bg-gradient-to-r from-red-50 to-blue-50 text-gray-900 border border-red-200 shadow-sm' 
                        : 'text-gray-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-blue-50 hover:text-gray-900 hover:shadow-md hover:scale-[1.02] hover:border hover:border-red-200'
                    } ${sidebarVisible ? 'px-4' : 'lg:px-2 lg:justify-center'}`}
                  >
                    <div className={`flex items-center transition-all duration-300 ${sidebarVisible ? 'space-x-3' : 'lg:space-x-0 lg:justify-center'}`}>
                      <item.icon size={20} className={`transition-all duration-300 ${
                        activeDropdown === index || item.children.some(child => child.active) 
                          ? 'text-red-600' 
                          : 'text-gray-500 group-hover:text-red-600 group-hover:scale-110'
                      }`} />
                      <span className={`font-medium transition-all duration-300 ${!sidebarVisible ? 'lg:opacity-0 lg:w-0 lg:overflow-hidden' : ''}`}>{item.name}</span>
                    </div>
                    <div className={`transition-all duration-300 ${!sidebarVisible ? 'lg:opacity-0 lg:w-0 lg:overflow-hidden' : ''}`}>
                      {activeDropdown === index ? (
                        <ChevronDown size={16} className="text-gray-400 group-hover:text-red-600 transition-all duration-300 group-hover:scale-110" />
                      ) : (
                        <ChevronRight size={16} className="text-gray-400 group-hover:text-red-600 transition-all duration-300 group-hover:scale-110" />
                      )}
                    </div>
                  </button>
                  {activeDropdown === index && (
                    <div className={`mt-2 space-y-1 border-l-2 border-gray-200 pl-4 ${sidebarVisible ? 'ml-8' : 'lg:ml-2'}`}>
                      {sidebarVisible ? (
                        item.children.map((child) => (
                          <button
                            key={child.name}
                            onClick={() => handleCardClick(child.href)}
                            className={`block w-full text-left px-4 py-2 rounded-lg text-sm transition-all duration-300 group/child relative overflow-hidden ${
                              child.active
                                ? 'bg-gradient-to-r from-red-500 to-blue-600 text-white shadow-lg font-medium'
                                : 'text-gray-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-blue-50 hover:text-gray-900 hover:shadow-md hover:scale-[1.02] hover:border hover:border-red-200'
                            }`}
                          >
                            {child.name}
                          </button>
                        ))
                      ) : (
                        <div className="absolute left-full ml-2 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 min-w-48">
                          {item.children.map((child) => (
                            <button
                              key={child.name}
                              onClick={() => handleCardClick(child.href)}
                              className={`block w-full text-left px-4 py-2 text-sm transition-all duration-300 group/child ${
                                child.active
                                  ? 'bg-gradient-to-r from-red-500 to-blue-600 text-white font-medium'
                                  : 'text-gray-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-blue-50 hover:text-gray-900 hover:shadow-sm'
                              }`}
                            >
                              {child.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => item.href && handleCardClick(item.href)}
                  className={`w-full flex items-center py-3 rounded-xl transition-all duration-300 group relative overflow-hidden ${
                    item.active
                      ? 'bg-gradient-to-r from-red-500 to-blue-600 text-white shadow-lg'
                      : 'text-gray-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-blue-50 hover:text-gray-900 hover:shadow-md hover:scale-[1.02] hover:border hover:border-red-200'
                  } ${sidebarVisible ? 'space-x-3 px-4' : 'lg:space-x-0 lg:px-2 lg:justify-center'}`}
                  title={!sidebarVisible ? item.name : ''}
                >
                  <item.icon size={20} className={`transition-all duration-300 ${
                    item.active 
                      ? 'text-white' 
                      : 'text-gray-500 group-hover:text-red-600 group-hover:scale-110'
                  }`} />
                  <span className={`font-medium transition-all duration-300 ${!sidebarVisible ? 'lg:opacity-0 lg:w-0 lg:overflow-hidden' : ''}`}>{item.name}</span>
                  {!sidebarVisible && (
                    <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap z-50 transform translate-x-2 group-hover:translate-x-0">
                      {item.name}
                    </div>
                  )}
                </button>
              )}
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className={`border-t border-red-200 bg-gradient-to-r from-red-50 to-blue-50 transition-all duration-300 ${sidebarVisible ? 'p-3' : 'lg:p-2'}`}>
          <div className={`bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-red-200 transition-all duration-300 ${sidebarVisible ? 'p-3' : 'lg:p-2'}`}>
            <div className={`flex items-center transition-all duration-300 ${sidebarVisible ? 'space-x-2' : 'lg:space-x-0 lg:justify-center'}`}>
              <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {(user?.name || user?.username || 'U').charAt(0).toUpperCase()}
              </div>
              <div className={`flex-1 transition-all duration-300 ${!sidebarVisible ? 'lg:opacity-0 lg:w-0 lg:overflow-hidden' : ''}`}>
                <p className="text-gray-900 font-medium text-sm whitespace-nowrap">{user?.name || user?.username || 'User'}</p>
                <p className="text-gray-600 text-xs whitespace-nowrap">{user?.email || 'user@example.com'}</p>
              </div>
              <div className={`w-1.5 h-1.5 bg-green-500 rounded-full transition-all duration-300 ${!sidebarVisible ? 'lg:opacity-0 lg:w-0 lg:overflow-hidden' : ''}`}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
